import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  CapaErrorCode,
  CapaServiceType,
  RequestRefundInput,
  CapaPurchaseOrderStatus,
  FetchPurchaseOrdersDocument,
  useUpdatePurchaseOrderStatusMutation,
  useDelayConfirmEscrowPurchaseMutation,
  FetchMyPaymentDataByQuotationUserDocument
} from '@generated/graphql';
import { ProcessEvent } from '@utils/customEvent';
import useQueryString from './useQueryString';
import useReview from './useReview';
import { FetchResult, InternalRefetchQueriesInclude } from '@apollo/client';

export enum ClientProcessActionType {
  '계좌번호복사' = '계좌번호복사',
  '결제취소' = '결제취소',
  '발주서재발행' = '발주서재발행',
  '환불요청' = '환불요청',
  '자동구매확정일연장' = '자동구매확정일연장',
  '구매확정' = '구매확정',
  '채팅으로이동' = '채팅으로이동',
  '리뷰작성' = '리뷰작성'
}

type AsyncCallback = ProcessEvent['detail']['callback'];

interface UpdatePurchaseOrderStatusCommonArgs {
  purchaseOrderId?: string;
  rfqId: string;
  quotationPartnerId: string;
  callback: AsyncCallback;
}

interface RequestCanclePaymentArgs extends UpdatePurchaseOrderStatusCommonArgs {
  requestRefund?: RequestRefundInput;
  statusToChange:
    | CapaPurchaseOrderStatus.RefundRequested
    | CapaPurchaseOrderStatus.CancelRequested;
}

interface DelayEscrowConfirmArgs extends UpdatePurchaseOrderStatusCommonArgs {
  extendReason?: string;
}

interface ConfirmPurchaseOrderArgs extends UpdatePurchaseOrderStatusCommonArgs {
  escrowVerifiedPW: string;
}

const getUpdatePurchaserOrderRefetchPolicy: (
  rfqId: string,
  quotationPartnerId: string
) => InternalRefetchQueriesInclude = (rfqId, quotationPartnerId) => {
  return [
    {
      query: FetchPurchaseOrdersDocument,
      variables: {
        input: {
          rfqId,
          quotationPartnerId
        }
      }
    },
    {
      query: FetchMyPaymentDataByQuotationUserDocument,
      variables: { input: {} }
    }
  ];
};

const useProcess = () => {
  const history = useHistory();
  const { getQueryString } = useQueryString<{ menu: string }>();
  const { onUpdateReview } = useReview();
  const [updatePurchaseOrderStatus] = useUpdatePurchaseOrderStatusMutation();
  const [delayEscrowConfirmation] = useDelayConfirmEscrowPurchaseMutation();

  const handleMutationResult = useCallback(
    (mutationResult: FetchResult, callback: AsyncCallback) => {
      const mutationNames = Object.keys(mutationResult.data ?? {});

      for (const mutationName of mutationNames) {
        const { result, errorMessage } = mutationResult.data?.[
          mutationName
        ] as {
          result: CapaErrorCode;
          errorMessage?: string;
        };

        callback(result, errorMessage);
      }
    },
    []
  );

  const requestPaymentCancle = useCallback(
    async ({
      rfqId,
      quotationPartnerId,
      purchaseOrderId,
      requestRefund,
      statusToChange,
      callback
    }: RequestCanclePaymentArgs) => {
      if (!purchaseOrderId) {
        return callback(CapaErrorCode.Error, '발주서 ID를 찾지 못했습니다.');
      }

      if (!requestRefund) {
        return callback(
          CapaErrorCode.Error,
          '결제취소/환불 사유를 입력해주세요.'
        );
      }

      try {
        const response = await updatePurchaseOrderStatus({
          variables: {
            input: {
              purchaseOrderId,
              statusToChange,
              requestRefund,
              service: CapaServiceType.Quotation
            }
          },
          refetchQueries: getUpdatePurchaserOrderRefetchPolicy(
            rfqId,
            quotationPartnerId
          ),
          awaitRefetchQueries: true
        });

        return handleMutationResult(response, callback);
      } catch {
        callback(
          CapaErrorCode.Error,
          '결제취소/환불 처리에 실패했습니다. 다시 시도해주세요.'
        );
      }
    },
    [updatePurchaseOrderStatus, handleMutationResult]
  );

  const delayEscrowConfirm = useCallback(
    async ({
      rfqId,
      quotationPartnerId,
      purchaseOrderId,
      extendReason,
      callback
    }: DelayEscrowConfirmArgs) => {
      if (!purchaseOrderId) {
        return callback(CapaErrorCode.Error, '발주서 ID를 찾지 못했습니다.');
      }

      if (!extendReason) {
        return callback(
          CapaErrorCode.Error,
          '구매확정일 연장 사유를 입력해주세요.'
        );
      }
      try {
        const response = await delayEscrowConfirmation({
          variables: {
            input: {
              purchaseOrderId,
              extendReason
            }
          },
          refetchQueries: getUpdatePurchaserOrderRefetchPolicy(
            rfqId,
            quotationPartnerId
          ),
          awaitRefetchQueries: true
        });

        return handleMutationResult(response, callback);
      } catch {
        callback(
          CapaErrorCode.Error,
          '구매확정일 연장에 실패했습니다. 다시 시도해주세요.'
        );
      }
    },
    [delayEscrowConfirmation, handleMutationResult]
  );

  const confirmPurchase = useCallback(
    async ({
      rfqId,
      quotationPartnerId,
      purchaseOrderId,
      escrowVerifiedPW,
      callback
    }: ConfirmPurchaseOrderArgs) => {
      if (!purchaseOrderId) {
        return callback(CapaErrorCode.Error, '발주서 ID를 찾지 못했습니다.');
      }

      try {
        const response = await updatePurchaseOrderStatus({
          variables: {
            input: {
              purchaseOrderId,
              escrowVerifiedPW,
              service: CapaServiceType.Quotation,
              statusToChange: CapaPurchaseOrderStatus.PurchaseConfirmed
            }
          },
          refetchQueries: getUpdatePurchaserOrderRefetchPolicy(
            rfqId,
            quotationPartnerId
          ),
          awaitRefetchQueries: true
        });
        return handleMutationResult(response, callback);
      } catch (e) {
        return callback(
          CapaErrorCode.Error,
          '구매확정처리에 실패했습니다. 다시 시도해주세요.'
        );
      }
    },
    [updatePurchaseOrderStatus, handleMutationResult]
  );

  const processListener = useCallback(
    async (e: Event) => {
      const { type, payload, callback } = (e as ProcessEvent).detail;
      switch (type) {
        case ClientProcessActionType.계좌번호복사: {
          const { bankAccount } = payload;
          if (!bankAccount) {
            return callback(
              CapaErrorCode.NoMessage,
              '복사할 계좌번호를 찾을 수 없습니다.'
            );
          }

          try {
            await navigator.clipboard.writeText(bankAccount);
            callback(CapaErrorCode.Success);
          } catch (e) {
            callback(CapaErrorCode.Error, e?.errorMessage);
          }
          break;
        }
        case ClientProcessActionType.결제취소: {
          const {
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            requestRefund
          } = payload;
          await requestPaymentCancle({
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            requestRefund,
            callback,
            statusToChange: CapaPurchaseOrderStatus.CancelRequested
          });
          break;
        }
        case ClientProcessActionType.발주서재발행: {
          const { chatRoomId } = payload;
          if (!chatRoomId)
            return callback(
              CapaErrorCode.Error,
              '채팅방 ID를 찾을 수 없습니다.'
            );
          history.push(
            `/chat/${chatRoomId}?${getQueryString({
              menu: 'create_purchaseorder'
            })}`
          );
          break;
        }
        case ClientProcessActionType.환불요청: {
          const {
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            requestRefund
          } = payload;
          await requestPaymentCancle({
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            requestRefund,
            callback,
            statusToChange: CapaPurchaseOrderStatus.RefundRequested
          });
          break;
        }
        case ClientProcessActionType.자동구매확정일연장: {
          const {
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            extendReason
          } = payload;
          await delayEscrowConfirm({
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            extendReason,
            callback
          });
          break;
        }
        case ClientProcessActionType.구매확정: {
          const {
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            escrowVerifiedPW
          } = payload;
          await confirmPurchase({
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            escrowVerifiedPW,
            callback
          });
          break;
        }
        case ClientProcessActionType.채팅으로이동: {
          const { chatRoomId } = payload;
          if (!chatRoomId)
            return callback(
              CapaErrorCode.Error,
              '채팅방 ID를 찾을 수 없습니다.'
            );
          history.push(`/chat/${chatRoomId}`);
          break;
        }
        case ClientProcessActionType.리뷰작성: {
          const {
            rfqId,
            quotationPartnerId,
            organizationName,
            projectName,
            partnerProfileLogoId
          } = payload;

          /** redux 스토어 활용 모달 실행, 모달 내에서 api 호출 후 모달 종료까지 처리 */
          onUpdateReview({
            open: true,
            rfqId,
            quotationPartnerId,
            profileId: partnerProfileLogoId,
            reviewId: '',
            projectName,
            organizationName
          });
          break;
        }
        default: {
          alert(`처리되지 않은 타입 : ${type}`);
        }
      }
    },
    [
      getQueryString,
      onUpdateReview,
      history,
      requestPaymentCancle,
      confirmPurchase,
      delayEscrowConfirm
    ]
  );

  useEffect(() => {
    window.addEventListener('process', processListener);
    return () => {
      window.removeEventListener('process', processListener);
    };
  }, [processListener]);
};

export default useProcess;
